<template lang="pug">
  div(:style="widthStyle")
    v-text-field.round-lg.yz-regular-body-sm(:class="[sizeClass, colorClass]" :style="colorStyle" v-model="modelValue" v-bind="filteredAttrs" v-on="$listeners" :rules="mergedRules" outlined, :hide-details="hideDetails")
      template(v-slot:append)
        slot(name="append")
      template(v-slot:append-outer)
        slot(name="append-outer")
      template(v-slot:prepend)
        slot(name="prepend")
      template(v-slot:prepend-inner)
        slot(name="prepend-inner")
</template>

<script>
export default {
  name: 'YzTextField',
  props: {
    value: {
      default: null
    },
    size: {
      type: String,
      default: 'lg',
      validator: (value) => ['lg', 'md', 'sm'].includes(value)
    },
    backgroundColor: {
      type: String,
      default: 'white'
    },
    width: {
      type: String,
      default: 'auto'
    },
    hideDetails: {
      type: [Boolean, String],
      default: "auto"
    },
    required: {
      type: Boolean,
      default: false
    },
    requiredErrorMessage: {
      type: String,
      default: 'This field is required'
    },
    rules: {
      type: Array,
      default: () => []
    },
    clickable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    modelValue: {
      get () {
        return this.value
      },
      set (newValue) {
        this.$emit('input', newValue)
      }
    },
    filteredAttrs() {
      // eslint-disable-next-line no-unused-vars
      const { outlined, filled, dense, label, hideDetails, shaped, singleLine, solo, ...attrs } = this.$attrs
      return attrs
    },
    sizeClass() {
      return `yz-text-field--${this.size}`
    },
    colorClass() {
      const classes = ['yz-text-field-custom-bg']
      if (this.clickable) {
        classes.push('yz-text-field-clickable')
      }
      return classes.join(' ')
    },
    colorStyle() {
      const style = {}
      
      // Set CSS custom property for background color
      if (this.backgroundColor) {
        let bgColor
        if (this.backgroundColor.startsWith('#')) {
          bgColor = this.backgroundColor
        } else if (this.backgroundColor.startsWith('var(')) {
          bgColor = this.backgroundColor
        } else if (this.backgroundColor.startsWith('yz-')) {
          bgColor = `var(--${this.backgroundColor})`
        } else {
          bgColor = `var(--yz-${this.backgroundColor})`
        }
        style['--yz-text-field-bg-color'] = bgColor
      }
      
      return style
    },
    widthStyle() {
      const style = {}
      
      if (this.width && this.width !== 'auto') {
        let widthValue
        
        // Handle predefined sizes
        if (this.width === 'full') {
          widthValue = '100%'
        } else if (this.width === 'fit-content') {
          widthValue = 'fit-content'
        } else {
          // Use the width value as-is for CSS units (px, %, rem, em, vw, vh, etc.)
          widthValue = this.width
        }
        
        style.width = widthValue
      }
      
      return style
    },
    mergedRules() {
      const rules = [...this.rules];
      
      // Skip required validation if field is disabled
      if (this.$attrs.disabled || this.$attrs.disabled === '') {
        return rules;
      }
      
      // If required is true, prepend the required validation rule
      if (this.required || this.required === '') {
        // Get input type from attrs, default to 'text'
        const inputType = this.$attrs.type || 'text';
        
        // Type-aware required validation
        const requiredRule = (v) => {
          switch (inputType) {
            case 'number':
              return (v != null && v !== '' && !isNaN(v)) || this.requiredErrorMessage;
            case 'email':
              return (v != null && v !== '' && String(v).trim() !== '') || this.requiredErrorMessage;
            case 'date':
            case 'datetime-local':
            case 'time':
              return (v != null && v !== '') || this.requiredErrorMessage;
            default:
              // For text and other types
              return (v != null && v !== '' && String(v).trim() !== '') || this.requiredErrorMessage;
          }
        };
        
        rules.unshift(requiredRule);
      }
      
      return rules;
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--yz-neutral-400);
    }
  }

  // Size-specific styles - Fixed selectors for v-input__slot
  &.yz-text-field--lg {
    .v-input__slot {
      min-height: 36px !important;
      height: 36px !important;
    }

    input {
      height: 36px !important;
    }
  }

  &.yz-text-field--md {
    .v-input__slot {
      min-height: 32px !important;
      height: 32px !important;
    }

    input {
      height: 32px !important;
    }
  }

  &.yz-text-field--sm {
    .v-input__slot {
      min-height: 28px !important;
      height: 28px !important;
    }

    input {
      height: 28px !important;
    }
  }

  .error--text {
    color: var(--yz-danger) !important;
    fieldset {
      border-color: var(--yz-danger);
    }
  }

  // Icon positioning for lg size (36px height)
  &.yz-text-field--lg {
    .v-input__prepend-outer,
    .v-input__append-outer,
    .v-input__prepend-inner,
    .v-input__append-inner {
      margin-top: 8px !important;
      margin-bottom: 8px !important;
      height: 20px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;

      .v-input__icon,
      .v-icon {
        height: 18px !important;
        width: 18px !important;
        font-size: 18px !important;
      }

      .v-icon__component {
        height: 18px !important;
        width: 18px !important;
      }
    }
  }

  // Icon positioning for md size (32px height)
  &.yz-text-field--md {
    .v-input__prepend-outer,
    .v-input__append-outer,
    .v-input__prepend-inner,
    .v-input__append-inner {
      margin-top: 6px !important;
      margin-bottom: 6px !important;
      height: 20px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;

      .v-input__icon,
      .v-icon {
        height: 18px !important;
        width: 18px !important;
        font-size: 18px !important;
      }

      .v-icon__component {
        height: 18px !important;
        width: 18px !important;
      }
    }
  }

  // Icon positioning for sm size (28px height)
  &.yz-text-field--sm {
    .v-input__prepend-outer,
    .v-input__append-outer,
    .v-input__prepend-inner,
    .v-input__append-inner {
      margin-top: 4px !important;
      margin-bottom: 4px !important;
      height: 20px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;

      .v-input__icon,
      .v-icon {
        height: 18px !important;
        width: 18px !important;
        font-size: 18px !important;
      }

      .v-icon__component {
        height: 18px !important;
        width: 18px !important;
      }
    }
  }

  // Ensure icons are centered vertically for all sizes
  .v-text-field--outlined {
    .v-input__prepend-inner,
    .v-input__append-inner,
    .v-input__prepend-outer,
    .v-input__append-outer {
      align-self: center;
    }
  }

  // Disabled state cursor
  input[disabled] {
    cursor: not-allowed !important;
  }
  
  // Readonly state cursor - allow parent to override for dropdowns
  input[readonly] {
    cursor: not-allowed;
  }

  // Clickable readonly state (for date pickers, dropdowns, etc.)
  &.yz-text-field-clickable input[readonly] {
    cursor: pointer !important;
  }

  // Custom background color support
  &.yz-text-field-custom-bg {
    .theme--light.v-input input,
    .theme--light.v-input textarea {
      background-color: var(--yz-text-field-bg-color, white) !important;
    }

    // Also target the input slot for outlined variant
    .v-input__slot {
      background-color: var(--yz-text-field-bg-color, white) !important;
    }
  }

  // Table cell specific adjustments
  .yz-data-table & {
    // Remove margins that can cause awkward spacing in table cells
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    
    // Ensure proper vertical centering in table cells
    .v-input {
      align-items: center !important;
    }

    // Better baseline alignment with dropdown components
    .v-input__slot {
      align-items: center !important;
      display: flex !important;
    }

    // Remove any default message padding that affects alignment
    .v-text-field__details {
      display: none !important;
    }
  }

}
</style>